import type { PlaceCoords } from '@bandido/shared/types';

/**
 * Coordenadas de una entrada de catálogo del hub, para el mapa del recorrido.
 *
 * El schema genérico de entrada de catálogo declara `lat`/`lng` OPCIONALES
 * ("si el proveedor la publica"), así que un catálogo sin geo no es un error:
 * degrada a "sin mapa", nunca a una coordenada inventada. Vive acá y no en
 * cada vertical porque la regla —y sobre todo la NEGATIVA a desempatar de
 * abajo— es la misma para todos los que resuelven una referencia comiteada
 * contra un catálogo (21.22: una sola definición).
 */

/** Coordenadas publicadas de una entrada, o `undefined` si no las trae. */
export function catalogEntryCoords(entry: {
  lat?: number;
  lng?: number;
}): PlaceCoords | undefined {
  return typeof entry.lat === 'number' && typeof entry.lng === 'number'
    ? { lat: entry.lat, lng: entry.lng }
    : undefined;
}

/**
 * La ÚNICA coordenada en la que coinciden los candidatos, o `undefined` si no
 * hay ninguna o hay más de una.
 *
 * No es un desempate: es la negativa a desempatar. Una referencia puede traer
 * más de un candidato —nombres de estación que no son únicos, un mismo código
 * publicado por dos proveedores— y elegir uno por un orden cualquiera
 * dibujaría un punto estable pero EQUIVOCADO, que el asesor no tiene cómo
 * notar. Candidatos sin geo publicada no aportan ni estorban.
 */
export function unambiguousCatalogCoords(
  candidates: { lat?: number; lng?: number }[],
): PlaceCoords | undefined {
  const distinct = new Map<string, PlaceCoords>();
  for (const candidate of candidates) {
    const coords = catalogEntryCoords(candidate);
    if (coords) distinct.set(`${coords.lat},${coords.lng}`, coords);
  }
  return distinct.size === 1 ? [...distinct.values()][0] : undefined;
}
